import { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  Box,
  Code,
  Spinner,
  Heading,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { generateStreamLink, StreamLinkDto } from '../api/streamApi';

interface StreamLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  locationName: string;
}

export function StreamLinkModal({
  isOpen,
  onClose,
  locationId,
}: StreamLinkModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [link, setLink] = useState<StreamLinkDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await generateStreamLink(locationId);
      setLink(result);
    } catch {
      setError(t('player.streamLink.generateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.streamUrl);
    setCopied(true);
    toast({
      title: t('player.streamLink.copied'),
      status: 'success',
      duration: 2000,
    });
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setLink(null);
    setError(null);
    setCopied(false);
    if (copiedTimeoutRef.current) {
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = null;
    }
    onClose();
  };

  const expiresDate = link
    ? new Date(link.expiresAt).toLocaleDateString()
    : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent data-testid="stream-link-modal">
        <ModalHeader>{t('player.streamLink.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!link && !isLoading && !error && (
            <VStack spacing={4} align="stretch">
              <Text>{t('player.streamLink.description')}</Text>
              <Button
                data-testid="generate-stream-link-btn"
                colorScheme="blue"
                onClick={handleGenerate}
              >
                {t('player.streamLink.generate')}
              </Button>
            </VStack>
          )}

          {isLoading && (
            <VStack py={8}>
              <Spinner size="lg" />
            </VStack>
          )}

          {error && (
            <VStack spacing={3} align="stretch">
              <Text color="red.500">{error}</Text>
              <Button onClick={handleGenerate} variant="outline">
                {t('player.streamLink.generate')}
              </Button>
            </VStack>
          )}

          {link && (
            <VStack spacing={4} align="stretch">
              <Box display="flex" justifyContent="center" py={1}>
                <Box bg="white" p={3} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                  <QRCodeSVG value={link.streamUrl} size={168} />
                </Box>
              </Box>
              <Box bg="gray.50" p={3} borderRadius="md">
                <Code
                  data-testid="stream-link-url"
                  fontSize="sm"
                  wordBreak="break-all"
                >
                  {link.streamUrl}
                </Code>
              </Box>

              <Button
                data-testid="copy-stream-link-btn"
                onClick={handleCopy}
                colorScheme={copied ? 'green' : 'blue'}
                variant="outline"
              >
                {copied
                  ? t('player.streamLink.copied')
                  : t('player.streamLink.copy')}
              </Button>

              <VStack spacing={2} align="stretch">
                <Heading size="sm">
                  {t('player.streamLink.instructions')}
                </Heading>
                {link.instructions?.vlc && (
                  <Box>
                    <Text fontWeight="bold">VLC</Text>
                    <Text fontSize="sm">{link.instructions.vlc}</Text>
                  </Box>
                )}
                {link.instructions?.sonos && (
                  <Box>
                    <Text fontWeight="bold">Sonos</Text>
                    <Text fontSize="sm">{link.instructions.sonos}</Text>
                  </Box>
                )}
                {link.instructions?.generic && (
                  <Box>
                    <Text fontWeight="bold">
                      {t('player.streamLink.otherPlayers')}
                    </Text>
                    <Text fontSize="sm">{link.instructions.generic}</Text>
                  </Box>
                )}
              </VStack>

              <Text fontSize="xs" color="gray.500">
                {t('player.streamLink.expires', { date: expiresDate })}
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose}>{t('player.streamLink.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
